import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { changeDefaultLanguage, changeDefaultLanguageCustom } from 'redux/actions/topBarActions';
import { convertErrorsArrayToString } from 'shared/helpers/helper';

import useApi from './useApi';

const SPANISHCODE = 'es-ES';
const ENGLISHCODE = 'en-US';
const CATALANCODE = 'ca-ES';
const EUSKERACODE = 'eu-ES';

function useLanguages(showDefaultLanguages) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const LANGUAGES = [
    {
      name: t('general_spanish'),
      value: SPANISHCODE,
    },
    {
      name: t('general_english'),
      value: ENGLISHCODE,
    },
    {
      name: t('general_catalan'),
      value: CATALANCODE,
    },
    {
      name: t('general_basque'),
      value: EUSKERACODE,
    },
  ];
  const languagesMap = new Map([
    [SPANISHCODE, t('general_spanish')],
    [ENGLISHCODE, t('general_english')],
    [CATALANCODE, t('general_catalan')],
    [EUSKERACODE, t('general_basque')],
  ]);
  const { cusNavId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { customAreaApi } = useApi();
  const [languages, setLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState('');
  const defaultValueLanguage = selectedDefaultLanguage;
  const { i18, isCustomer } = useSelector((state) => state.auth);

  const [{ loading }, getLanguages] = customAreaApi(
    {
      url: `${window.API_V1}/cus/langs/${cusNavId}`,
      method: 'GET',
    },
    { manual: true }
  );

  const getLanguageNameByCode = (languageCode) => {
    return languagesMap.get(languageCode) ?? '';
  };

  const loadDefaultLanguages = () => {
    setLanguages(LANGUAGES);
    setDefaultLanguage(defaultValueLanguage);
  };

  useEffect(() => {
    setLanguages(LANGUAGES);
  }, [selectedDefaultLanguage]);

  const loadCusLanguages = async () => {
    try {
      const {
        data: { errors, data },
      } = await getLanguages();
      if (errors.length > 0) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      if (data.length > 0) {
        const cusLanguages = data.map((language) => ({
          name: getLanguageNameByCode(language.id),
          value: language.id,
        }));
        setLanguages(cusLanguages);
        if (isCustomer) {
          i18n.changeLanguage(
            defaultValueLanguage || data.find((language) => language.default)?.id
          );
          dispatch(
            changeDefaultLanguage(
              defaultValueLanguage || data.find((language) => language.default)?.id
            )
          );
          dispatch(changeDefaultLanguageCustom(data.find((language) => language.default)?.id));
        } else {
          i18n.changeLanguage(defaultValueLanguage || i18);
          dispatch(changeDefaultLanguage(defaultValueLanguage || i18));
        }
        setDefaultLanguage(defaultValueLanguage || data.find((language) => language.default)?.id);
      } else {
        loadDefaultLanguages();
      }
    } catch (error) {
      console.error('Error getting customer languages: ', error);
    }
  };

  useEffect(() => {
    if (showDefaultLanguages) {
      loadDefaultLanguages();
      return;
    }
    loadCusLanguages();
  }, [showDefaultLanguages]);

  return { languages, defaultLanguage, loading };
}

export default useLanguages;
